import { createRoot } from 'react-dom/client';
import { type IThemeContext, ThemeContext } from '@/state/Theme';
import injectColorPalette from '@/functions/injectColorPalette';

import Accordion from '@/components/Accordion';
import CarbonEmissions from '@/components/CarbonEmissions';
import Locator from '@/components/Map/Locator';
import LocationMap from '@/components/Map/LocationMap';
import ScrollerButton from '@/components/ScrollerButton';
import SearchResults from '@/components/Search/SearchResults';
import SearchForm from '@/components/Search/SearchForm';
import Header from '@/components/Header';
import PageFeedback from '@/components/PageFeedback';
import Sidebar from '@/components/Sidebar';
import FactBox from '@/components/FactBox';
import HeroBanner from '@/components/HeroBanner';
import PageHeader from '@/components/PageHeader';
import TwoColumnText from '@/components/TwoColumnText';
import BannerLinkGrid from '@/components/BannerLinkGrid';
import ContentWithTitle from '@/components/ContentWithTitle';
import LandingTiles from '@/components/LandingTiles';
import Promotion from '@/components/Promotion/Promotion';
import AppPromoBlock from '@/components/AppPromoBlock/AppPromoBlock';
import IconCardQuickLinks from '@/components/IconCardQuickLinks/IconCardQuickLinks';
import FooterColumn from '@/components/FooterColumn';
import FooterGrid from '@/components/FooterGrid';
import ThreeColumnLink from '@/components/ThreeColumnLink';
import TextOnlyCardLinkBlock from '@/components/TextOnlyCardLinkBlock';
import TaskCTABlock from '@/components/TaskCTABlock';
import ContextBlock from '@/components/ContextBlock';
import FeatureCardPromotional from '@/components/FeatureCardPromotional';
import ContentGrid from '@/components/ContentGrid';
import BannerBlock from '@/components/BannerBlock';
import FeatureCardStandard from '@/components/FeatureCardStandard';
import ToggleButton from '@/components/ToggleButtons';
import LinksBlock from '@/components/LinksBlock/LinksBlock';
import PromotionAccordion from '@/components/PromotionAccordion/PromotionAccordion';

const Registry = {
  components: {
    accordion: Accordion,
    'carbon-emissions': CarbonEmissions,
    'content-with-title': ContentWithTitle,
    sidebar: Sidebar,
    header: Header,
    locator: Locator,
    'hero-banner': HeroBanner,
    'landing-tiles': LandingTiles,
    'page-feedback': PageFeedback,
    'page-header': PageHeader,
    'promotion-accordion': PromotionAccordion,
    'scroller-button': ScrollerButton,
    'search-results': SearchResults,
    'fact-box': FactBox,
    'search-form': SearchForm,
    'location-map': LocationMap,
    'two-column-text': TwoColumnText,
    'app-promo': AppPromoBlock,
    'banner-link-grid': BannerLinkGrid,
    'links-block': LinksBlock,
    'promotion-block': Promotion,
    'icon-card-quick-links': IconCardQuickLinks,
    'footer-column': FooterColumn,
    'footer-grid': FooterGrid,
    'three-column-link': ThreeColumnLink,
    'text-only-card-link-block': TextOnlyCardLinkBlock,
    'task-cta-block': TaskCTABlock,
    'context-block': ContextBlock,
    'feature-card-promotional': FeatureCardPromotional,
    'content-grid': ContentGrid,
    'banner-block': BannerBlock,
    'feature-card-standard': FeatureCardStandard,
    'toggle-button': ToggleButton,
  },
  register: (key, component) => {
    Registry.components[key] = component;
  },
  merge: (components) => {
    Registry.components = { ...Registry.components, ...components };
  },
  applyTo: (document: Document, brandTheme: IThemeContext) => {
    const body = document.querySelector('body');

    if (body) {
      injectColorPalette(brandTheme);

      Object.keys(Registry.components).map((name) => {
        const el = document.querySelectorAll(`[data-component="${name}"]`);

        el.forEach((element) => {
          const Component = Registry.components[name];

          if (element) {
            try {
              let componentProps = {};
              const staticProps = element.hasAttribute('data-props')
                ? element.getAttribute('data-props')
                : '';

              if (staticProps.substring(0, 1) === '{') {
                componentProps = JSON.parse(staticProps);
              } else if (staticProps.substring(0, 7) === '__PROPS') {
                componentProps = eval(staticProps);
              }

              const component = (
                <ThemeContext.Provider value={brandTheme}>
                  <Component {...componentProps} />
                </ThemeContext.Provider>
              );
              const root = createRoot(element);
              root.render(component);
            } catch (e) {
              console.error(`Error rendering component ${name}`);
              console.error(e);
            }
          }
        });
      });

      setTimeout(() => {
        body.classList.add('energized');
      }, 100);
    }
  },
};

export default Registry;
